import { Card, Pointer } from '@yleisradio/areena-types';
import {
  IUserApiResponse,
  IUserConsentApiResponse,
} from '@yleisradio/tunnus-types';
import { ApplicationResolution } from '../client/applicationResolution';
import { ElasticAPMRumOptions } from '../server/elasticApmOptions';

export type Avatar = {
  id: string;
  altTexts?: {
    [language: string]: string;
  };
};

export type Mode =
  | 'hbbtv'
  | 'webos'
  | 'tizen'
  | 'philips'
  | 'vestel'
  | 'panasonic'
  | 'telia'
  | 'elisa'
  | 'hisense'
  | 'tivo'
  | 'sony'
  | 'ps4'
  | 'ps5';

export interface YleApiCredentials {
  id: string | undefined;
  key: string | undefined;
}

export type YleApiCredentialsPerMode = {
  [P in Mode]: YleApiCredentials;
};

export interface ProfileCookies {
  yletestlogin?: string;
  ylelogin?: string;
}

export type Consents = IUserConsentApiResponse;

export type LoggedInUser = IUserApiResponse;

export type LoggedInSubAccount = Omit<LoggedInUser, 'username'> & {
  parentId: string;
  displayName: string;
  nick: null;
  nickUpdatedAt: null;
};

export interface ErrorObject {
  message: string | Event;
  source: string | undefined;
  lineno: number | undefined;
  colno: number | undefined;
  error: Error | undefined;
}

export type YleLeanback = {
  VERSION: string;
  PUBLIC_PATH_PREFIX: string;
  RESOLUTION: ApplicationResolution;
  E2E_TESTS: boolean;
  MODE: Mode;
  elasticAPMOptions: ElasticAPMRumOptions;
  clientYleApiCredentials: YleApiCredentials;
  MUX_ENV_KEY: string;
  BRAZE_API_KEY: string;
  errorStack?: Array<ErrorObject>;
  originalWindowOnError?: OnErrorEventHandler;
  FEATURE_BRAZE: boolean;
};

export type ProfileIcon = {
  type: 'color' | 'image';
  value: string;
};

export type ProfileIconColor = ProfileIcon & {
  type: 'color';
};

export type ProfileIconImage = ProfileIcon & {
  type: 'image';
};

export type Profile = {
  age: number;
  consent: LoggedInUser['consent']; // although not visible, this is used in 5xx login API-error recovery
  cookie: string | undefined;
  displayName: string;
  icon: ProfileIconColor | ProfileIconImage;
  initials: string;
  parentId: string | null;
  userId: string;
  username: string | undefined;
};

export type SubProfile = Omit<Profile, 'parentId' | 'username'> & {
  parentId: string;
  username: undefined;
};

export type ParentProfile = Omit<Profile, 'parentId' | 'icon' | 'cookie'> & {
  icon: ProfileIconColor;
  parentId: null;
  cookie: string;
  username: string;
};

export const hbbTvMode = 'hbbtv';
export const webOsMode = 'webos';
export const tizenMode = 'tizen';
export const philipsMode = 'philips';
export const vestelMode = 'vestel';
export const panasonicMode = 'panasonic';
export const teliaMode = 'telia';
export const elisaMode = 'elisa';
export const hisenseMode = 'hisense';
export const tivoMode = 'tivo';
export const sonyMode = 'sony';
export const ps4Mode = 'ps4';
export const ps5Mode = 'ps5';

export type AreenaApiClient = 'yle-areena-leanback';

/**
 * Card guaranteed to include a pointer
 */
export type CardWithPointer = Card & {
  pointer: Pointer;
  menuSource?: Pointer;
};

export type CardWithSubcardPointer = Card & {
  cards: Card[];
};
